import { useState } from 'react';
import * as React from 'react';
import ReactCrop, { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Flex } from 'reflexbox';

import { Button } from '@treams/treams-ui';

import { IImageSpecification, ImageShape } from 'packages/kakapo-ui-kit/types/Image';
import Column from 'packages/kakapo-ui-kit/components/grid/Column';
import Nullable from 'packages/kakapo-ui-services/types/Nullable';
import ImageService from 'packages/kakapo-ui-services/services/common/ImageService';
import { ParagraphSmall } from 'packages/kakapo-ui-kit/components/common/Paragraph';

export interface IProps {
	fileString: string;

	imageSpecification: IImageSpecification;

	onImageResize: (fileBlob: Blob) => void;
	onError: (error: Error) => void;
	onFinish: () => void;
}

const ImageResizerContainer = styled.div`
	max-width: 100%;
	background: ${(props) => props.theme.colors.lightBlueLight};
	padding: 4px;

	img {
		max-height: 360px;
		display: block;
		margin-bottom: 0;
	}
`;

const ImageResizerPreview = styled.img`
	display: block;
	max-height: 100px !important;
	max-width: 80%;
`;

const cropImage = async (
	imageRef: HTMLImageElement,
	crop: Crop,
	imageOutputFileType: string,
): Promise<Blob> => {
	if (imageRef === null || crop.width == null || crop.height == null) {
		throw new Error('The property imageRef and crop dimensions are required.');
	}

	const frame = ImageService.createImageResizeFrame(
		crop,
		{
			width: imageRef.width,
			height: imageRef.height,
		},
		{
			width: imageRef.naturalWidth,
			height: imageRef.naturalHeight,
		},
	);

	const canvas = document.createElement('canvas');
	canvas.width = frame.destination.width;
	canvas.height = frame.destination.height;
	const ctx = canvas.getContext('2d');

	ctx!.drawImage(
		imageRef,
		frame.source.x,
		frame.source.y,
		frame.source.width,
		frame.source.height,
		frame.destination.x,
		frame.destination.y,
		frame.destination.width,
		frame.destination.height,
	);

	return new Promise((resolve) => {
		canvas.toBlob(
			(blob) => {
				resolve(blob!);
			},
			imageOutputFileType,
			1,
		);
	});
};

export default function UploadImageDialog({
	fileString,
	imageSpecification,
	onImageResize,
	onError,
	onFinish,
}: IProps) {
	const { t } = useTranslation();
	const [crop, setCrop] = useState<Crop>();
	const [imageRef, setImageRef] = useState<Nullable<HTMLImageElement>>(null);
	const [croppedImagePreviewBlob, setCroppedImagePreviewBlob] = useState<Nullable<Blob>>(null);
	const imageOutputFileType = 'image/png';

	const onCropComplete = async (newCrop: Crop) => {
		if (imageRef == null) {
			throw new Error('Could not complete crop. Image element is removed by the DOM.');
		}

		try {
			const imageBlob = await cropImage(imageRef, newCrop, imageOutputFileType);
			setCroppedImagePreviewBlob(imageBlob);
			onImageResize(imageBlob);
		} catch (error) {
			onError(error);
		}
	};

	const onImageLoaded = (img: HTMLImageElement): boolean => {
		setImageRef(img);

		// Applies a default crop selection with the aspect ratio used in state
		const updatedCrop: Crop = {
			unit: 'px',
			width: Math.min(img.width, img.height * imageSpecification.aspectRatio),
			height: Math.min(img.height, img.width / imageSpecification.aspectRatio),
			x: 0,
			y: 0,
		};
		setCrop(updatedCrop);

		// If height is already defined, the image should be pre-cropped. Otherwise the upload doesn't work
		if (updatedCrop.height !== undefined) {
			cropImage(img, updatedCrop, imageOutputFileType).then((imageBlob) => {
				setCroppedImagePreviewBlob(imageBlob);
				onImageResize(imageBlob);
			});
		}

		// Must return false when the state crop object is modified by this callback
		return false;
	};

	const onFinishButtonClick = (event: React.MouseEvent) => {
		event.preventDefault();
		onFinish();
	};

	const frame = ImageService.createImageResizeFrame(
		crop,
		{
			width: imageRef?.width ?? 0,
			height: imageRef?.height ?? 0,
		},
		{
			width: imageRef?.naturalWidth ?? 0,
			height: imageRef?.naturalHeight ?? 0,
		},
	);

	return (
		<>
			<ImageResizerContainer>
				<Flex alignItems="center" textAlign="center" justifyContent="center" p={2}>
					{imageSpecification.minimumWidth && (
						<ParagraphSmall>
							{t('images.minimumWidth', {
								minimumWidth: imageSpecification.minimumWidth,
								currentWidth: frame?.destination.width ?? 0,
							})}
						</ParagraphSmall>
					)}
				</Flex>
				<ReactCrop
					crop={crop}
					circularCrop={imageSpecification.shape === ImageShape.Circlular}
					keepSelection
					onComplete={onCropComplete}
					onChange={(newCrop: Crop) => setCrop(newCrop)}
				>
					<img
						src={fileString}
						onLoad={(event) => onImageLoaded(event.currentTarget)}
						alt="preview"
					/>
				</ReactCrop>

				{croppedImagePreviewBlob && (
					<ImageResizerPreview src={URL.createObjectURL(croppedImagePreviewBlob)} alt="preview" />
				)}
			</ImageResizerContainer>

			<Column textAlign="center" mt={4}>
				<Flex justifyContent="flex-end">
					<Button
						onClick={onFinishButtonClick}
						title={[
							t('common.forms.file-upload.finishAndSetNewImage'),
							t(`common.forms.file-upload.imageKey.${imageSpecification.key}`),
						].join(' ')}
					/>
				</Flex>
			</Column>
		</>
	);
}
